import { isAutoLoginOrigin } from './autoLoginHosts';

/**
 * Client for the public content index feed built by the content site
 * (content/src/pages/content-index.json.ts → stephansmith.me/content-index.json).
 * Lists every published page Stephan can recommend to a connection.
 */

export type ContentIndexCollection =
  | 'insights'
  | 'buildInPublic'
  | 'playbook'
  | 'playbookPosts'
  | 'leadMagnets';

export type ContentIndexItem = {
  id: string;
  collection: ContentIndexCollection;
  title: string;
  subtitle: string;
  description: string;
  url: string;
  pubDate: string | null;
  section: string | null;
  /** Editorial tier 0–3 — see content/docs/content/content-maturity.md */
  maturity: 0 | 1 | 2 | 3;
  /** Present on live feed; optional for dev snapshot compatibility. */
  gated?: boolean;
};

export type ContentIndex = {
  generatedAt: string;
  count: number;
  items: ContentIndexItem[];
};

/**
 * Dev falls back to the snapshot in engine-ui/public/ (same-origin, no CORS,
 * works before the content site deploys the live feed). Refresh it with:
 *   cd content && npm run build && cp dist/content-index.json ../engine-ui/public/
 */
export const CONTENT_INDEX_URL =
  (import.meta.env.VITE_CONTENT_INDEX_URL as string | undefined) ||
  (import.meta.env.DEV ? '/content-index.json' : 'https://stephansmith.me/content-index.json');

export const COLLECTION_LABELS: Record<ContentIndexCollection, string> = {
  insights: 'Insight',
  buildInPublic: 'Build in public',
  playbook: 'Playbook tool',
  playbookPosts: 'Playbook guide',
  leadMagnets: 'Lead magnet',
};

/** Editorial tier — see content/docs/content/content-maturity.md */
export const MATURITY_LABELS: Record<0 | 1 | 2 | 3, string> = {
  0: 'Draft',
  1: 'Live',
  2: 'Polished',
  3: 'Benchmark',
};

export async function fetchContentIndex(signal?: AbortSignal): Promise<ContentIndex> {
  const res = await fetch(CONTENT_INDEX_URL, { signal });
  if (!res.ok) {
    throw new Error(`Content index fetch failed (${res.status}) — is ${CONTENT_INDEX_URL} deployed?`);
  }
  const text = await res.text();
  let json: ContentIndex;
  try {
    json = JSON.parse(text) as ContentIndex;
  } catch {
    throw new Error(
      `Content index at ${CONTENT_INDEX_URL} returned a non-JSON page — the content site probably hasn't deployed the feed yet.`
    );
  }
  if (!Array.isArray(json.items)) {
    throw new Error('Content index response missing items[]');
  }
  return json;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain text → simple paragraph HTML (blank lines split paragraphs). */
function textToParagraphs(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(
      (p) =>
        `<p style="margin:0 0 16px;font-size:15px;line-height:1.6;color:#1f2937;">${escapeHtml(p).replace(/\n/g, '<br/>')}</p>`
    )
    .join('\n');
}

export type RecommendationItem = {
  title: string;
  subtitle: string;
  url: string;
};

export type RecommendationEmailInput = RecommendationItem & {
  intro: string;
  /**
   * When true, appends `?token={{{manage_token}}}` on owned-site links so
   * the reader lands on the content page and signs in silently (see
   * content/src/lib/emailTokenLogin.ts).
   */
  autoLogin?: boolean;
};

export type RecommendationsEmailInput = {
  intro: string;
  items: RecommendationItem[];
  autoLogin?: boolean;
};

/** Placeholder the send worker replaces with the subscriber's manage token. */
export const MANAGE_TOKEN_PLACEHOLDER = '{{{manage_token}}}';

export function contentIndexItemKey(it: Pick<ContentIndexItem, 'collection' | 'id'>): string {
  return `${it.collection}:${it.id}`;
}

/** Content page URL with `?token=` appended for one-click login (owned sites only). */
export function recommendationLinkHref(url: string, autoLogin: boolean): string {
  if (!autoLogin || !isAutoLoginOrigin(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${MANAGE_TOKEN_PLACEHOLDER}`;
}

/** Preview in the UI — shows content URL + token slot (real token injected at send time). */
export function previewRecommendationLink(url: string, sampleToken = '…'): string {
  return recommendationLinkHref(url, true).replace(MANAGE_TOKEN_PLACEHOLDER, sampleToken);
}

/** Real manage token on an owned-site URL (same shape as sent recommendation emails). */
export function contentAccessUrl(pageUrl: string, manageToken: string): string {
  if (!manageToken.trim()) return pageUrl;
  if (!isAutoLoginOrigin(pageUrl)) return pageUrl;
  const sep = pageUrl.includes('?') ? '&' : '?';
  return `${pageUrl}${sep}token=${encodeURIComponent(manageToken.trim())}`;
}

function recommendationLinkHtml({ title, url }: RecommendationItem, autoLogin: boolean): string {
  const href = escapeHtml(recommendationLinkHref(url, autoLogin));
  return `<p style="margin:0 0 8px;font-size:15px;line-height:1.6;color:#1f2937;"><a href="${href}" style="color:#059669;text-decoration:underline;">${escapeHtml(title)}</a></p>`;
}

/** Inline-styled HTML body: intro paragraphs + plain text links. */
export function buildRecommendationsEmailHtml({
  intro,
  items,
  autoLogin = true,
}: RecommendationsEmailInput): string {
  const introHtml = intro.trim() ? textToParagraphs(intro) : '';
  const links = items
    .filter((it) => it.title.trim() && it.url.trim())
    .map((it) => recommendationLinkHtml(it, autoLogin));
  const signoff = `<p style="margin:16px 0 0;font-size:15px;line-height:1.6;color:#1f2937;">Thanks,<br/>Stephan</p>`;
  return [introHtml, ...links, signoff].filter(Boolean).join('\n');
}

/** Inline-styled HTML body for a single content recommendation send. */
export function buildRecommendationEmailHtml({
  intro,
  title,
  subtitle,
  url,
  autoLogin = true,
}: RecommendationEmailInput): string {
  return buildRecommendationsEmailHtml({
    intro,
    items: [{ title, subtitle, url }],
    autoLogin,
  });
}
